/**
 * NMS Tabbed Collections Component
 * Handles tab switching functionality with keyboard navigation and accessibility
 * Follows NMS component instance architecture for performance and maintainability
 */

class NMSTabbedCollections extends window.NMSBaseClass {
  #currentTab = 'men';

  constructor(element) {
    super();

    // REQUIRED: Store element reference for scoped operations
    this.element = element;
    this.sectionId = element.dataset.sectionId || `section-${Date.now()}`;
    this.componentId = `tabbed-collections-${this.sectionId}`;

    // REQUIRED: Register instance in global namespace for external access
    window.neemans = window.neemans || {};
    window.neemans.tabbedCollections = window.neemans.tabbedCollections || {};
    window.neemans.tabbedCollections[this.sectionId] = this;

  }

  init() {
    // REQUIRED: Override base init to scope operations to this.element
    this.#setupComponentRefs();
    this.#bindComponentEvents();
    this.#setupKeyboardNavigation();
    this.#initializeTabState();
  }

  destroy() {
    super.destroy();
    // REQUIRED: Clean up global reference
    if (window.neemans.tabbedCollections?.[this.sectionId]) {
      delete window.neemans.tabbedCollections[this.sectionId];
    }
  }

  // REQUIRED: Component-scoped ref setup (override base class)
  #setupComponentRefs() {
    this.refs = this.refs || {};
    const refElements = this.element.querySelectorAll('[ref]');

    for (const element of refElements) {
      const refName = element.getAttribute('ref');

      if (refName.endsWith('[]')) {
        const arrayName = refName.slice(0, -2);
        if (!this.refs[arrayName]) this.refs[arrayName] = [];
        this.refs[arrayName].push(element);
      } else {
        this.refs[refName] = element;
      }
    }
  }

  // REQUIRED: Component-scoped event binding (override base class)
  #bindComponentEvents() {
    const clickElements = this.element.querySelectorAll('[data-nms-click]');

    for (const element of clickElements) {
      const handlerPath = element.getAttribute('data-nms-click');
      const [methodName, ...args] = handlerPath.split('/').filter(Boolean);

      if (this[methodName] && typeof this[methodName] === 'function') {
        element.addEventListener('click', event => {
          this[methodName].call(this, event, ...args);
        });
      }
    }
  }

  #setupKeyboardNavigation() {
    if (!this.refs.toggleButtons) return;

    this.element.addEventListener('keydown', (event) => {
      if (!event.target.matches('[ref="toggleButtons[]"]')) return;

      const tabs = this.refs.toggleButtons;
      const currentIndex = tabs.indexOf(event.target);
      let nextIndex;

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          nextIndex = currentIndex > 0 ? currentIndex - 1 : tabs.length - 1;
          tabs[nextIndex].focus();
          this.#switchTab(tabs[nextIndex].dataset.tab);
          break;
        case 'ArrowRight':
          event.preventDefault();
          nextIndex = currentIndex < tabs.length - 1 ? currentIndex + 1 : 0;
          tabs[nextIndex].focus();
          this.#switchTab(tabs[nextIndex].dataset.tab);
          break;
        case 'Home':
          event.preventDefault();
          tabs[0].focus();
          this.#switchTab(tabs[0].dataset.tab);
          break;
        case 'End':
          event.preventDefault();
          tabs[tabs.length - 1].focus();
          this.#switchTab(tabs[tabs.length - 1].dataset.tab);
          break;
      }
    });
  }

  #initializeTabState() {
    // Set initial active tab
    this.#currentTab = 'men';
    this.#updateTabStates();
  }

  // Public event handler - auto-bound via data-nms-click
  handleTabClick(event, tabName) {
    event.preventDefault();

    if (this.#currentTab === tabName) return;

    this.#switchTab(tabName);

    // Dispatch custom event following NMS pattern
    this.dispatchNMSEvent('tab-changed', {
      previousTab: this.#currentTab,
      currentTab: tabName,
      sectionId: this.sectionId
    });
  }

  // Private method for tab switching
  #switchTab(tabName) {
    this.#currentTab = tabName;
    this.#updateTabStates();
  }

  #updateTabStates() {
    if (!this.refs.toggleButtons || !this.refs.contentContainers) return;

    // Update button states
    for (const button of this.refs.toggleButtons) {
      const isActive = button.dataset.tab === this.#currentTab;

      // Update ARIA attributes
      button.setAttribute('aria-selected', isActive ? 'true' : 'false');

      // Update CSS classes for toggle styling
      button.classList.toggle('nms-tabbed-collections__toggle-button--active', isActive);

      if (isActive) {
        button.classList.remove('nms-button--secondary');
        button.classList.add('nms-button--primary');
      } else {
        button.classList.remove('nms-button--primary');
        button.classList.add('nms-button--secondary');
      }
    }

    // Update content visibility
    for (const content of this.refs.contentContainers) {
      const isActive = content.dataset.tabContent === this.#currentTab;
      content.classList.toggle('nms-tabbed-collections__content--active', isActive);
      
      // Fix: Clear conflicting inline display styles to let CSS classes work
      content.style.display = '';
    }
  }

  // Public API for external access
  switchToTab(tabName) {
    if (tabName === 'men' || tabName === 'women') {
      this.#switchTab(tabName);
    }
  }

  getCurrentTab() {
    return this.#currentTab;
  }
}

// REQUIRED: Standard component initialization pattern
function initNMSTabbedCollections() {
  // Check for base class availability
  if (!window.NMSBaseClass) {
    console.error('NMS Tabbed Collections: NMSBaseClass not found. Make sure nms-base-class.js loads first.');
    return;
  }

  const elements = document.querySelectorAll('[data-nms-tabbed-collections]');

  for (const element of elements) {
    if (!element.hasAttribute('data-initialized')) {
      try {
        const instance = new NMSTabbedCollections(element);
        instance.init();
        element.setAttribute('data-initialized', 'true');
      } catch (error) {
        console.error('Failed to initialize NMS Tabbed Collections:', error, element);
      }
    }
  }
}

// Auto-initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initNMSTabbedCollections);
} else {
  initNMSTabbedCollections();
}

// Re-initialize when new content is added (theme editor, AJAX, etc.)
document.addEventListener('nms:content-updated', initNMSTabbedCollections);

// Export for debugging and manual initialization
window.initNMSTabbedCollections = initNMSTabbedCollections;

// Global API for accessing instances
window.getNMSTabbedCollections = function(sectionId) {
  if (sectionId) {
    return window.neemans?.tabbedCollections?.[sectionId] || null;
  }
  return window.neemans?.tabbedCollections || {};
};
